import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Checkbox,
  Center,
  Spinner
} from '@chakra-ui/react';
import { validateResetToken, resetPassword } from '../services/AuthService';

const PasswordReset = () => {
  const { registryId, token } = useParams<{ registryId: string; token: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }

      try {
        const result = await validateResetToken(token);
        setIsValidToken(result.valid);
      } catch (error) {
        console.error('Token validation error:', error);
        setIsValidToken(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in both password fields',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords Don\'t Match',
        description: 'Please make sure both passwords are identical',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 8 characters long',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!token) {
      toast({
        title: 'Invalid Token',
        description: 'Reset token is missing',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsResetting(true);

    try {
      const success = await resetPassword(token, newPassword);
      
      if (success) {
        toast({
          title: 'Password Reset Successful',
          description: 'Your password has been updated.',
          status: 'success',
          duration: 5000,
        });
        
        // Redirect to the registry page after a short delay
        setTimeout(() => {
          navigate(`/registry/${registryId}`);
        }, 500);
      } else {
        toast({
          title: 'Reset Failed',
          description: 'Failed to reset password. The link may have expired.',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: 'Error',
        description: 'There was a problem resetting your password. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsResetting(false);
    }
  };

  if (isValidating) {
    return (
      <Box bg="mamaFuel.cream" minH="100vh" py={8}>
        <Container maxW="md">
          <Center py={10}>
            <VStack spacing={4}>
              <Spinner size="xl" />
              <Text>Validating reset link...</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (!isValidToken) {
    return (
      <Box bg="mamaFuel.cream" minH="100vh" py={8}>
        <Container maxW="md">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Invalid Reset Link
            </AlertTitle>
            <Button mt={4} colorScheme="blue" onClick={() => navigate(`/registry/${registryId}`)}>
              Back to MommaFuel
            </Button>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="mamaFuel.cream" minH="100vh" py={8}>
      <Container maxW="md">
        <Box
          borderWidth="1px"
          borderRadius="lg"
          p={8}
          bg="white"
          shadow="md"
        >
          <VStack spacing={6} as="form" onSubmit={handlePasswordReset}>
            <Heading size="lg" textAlign="center">
              Reset Your Password
            </Heading>
            
            <Text textAlign="center" color="gray.600">
              Enter your new password below
            </Text>

            <FormControl isRequired>
              <FormLabel>New Password</FormLabel>
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Confirm New Password</FormLabel>
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </FormControl>

            <Checkbox 
              isChecked={showPasswords}
              alignSelf="flex-start"
              onChange={(e) => setShowPasswords(e.target.checked)}
              colorScheme="green"
            >
              Show Passwords
            </Checkbox>

            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              width="full"
              isLoading={isResetting}
              loadingText="Resetting Password..."
              isDisabled={!newPassword || !confirmPassword}
            >
              Reset Password
            </Button>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default PasswordReset;